import { ArrowLeftOutlined, EyeInvisibleOutlined } from '@ant-design/icons'
import { Button, Tag, notification } from 'antd'
import React from 'react'
import { MalindexBesok, MalindexConfig } from '../../../api/malindex/malindexApi'
import ViewHeader from '../../../components/viewHeader/ViewHeader'
import { arKartlaggning, kronologiskt, nyaMalVid, svarForOmrade } from '../malindexCalc'
import { besoksEtikett, efterVarde, formateraDatum, Sifferruta, SVARANDE_ETIKETT, VIKTIGT_ORD } from '../visning'
import RaderaBesokModal from './RaderaBesokModal'

/**
 * BesokVy – ett enskilt besök i sin helhet.
 *
 * Besökslistans utfällning svarar på "vad hände vid det där besöket?" i prosa. Den kan
 * inte bära vad besöket FAKTISKT innehåller: svaren område för område lagras men syntes
 * ingenstans i appen. Ville man veta vad brukaren svarade om trygghet i mars fanns
 * ingen väg dit.
 *
 * Det är också den enda plats där underlaget till beställningen hör hemma, eftersom
 * exporten behöver just det innehållet.
 *
 * TYPOGRAFI. Fyra nivåer, inte fler:
 *   sidrubrik      ViewHeader, samma som "Målindex" på översikten
 *   sektionsetikett 12px versaler, grå – namnger en grupp, bär inget innehåll
 *   innehåll       16px gray-800
 *   sekundärt      14px gray-500 – datum, källor, förklaringar
 * Sidan hade tidigare fem storlekar och sex gråtoner, vilket gjorde att ögat inte
 * kunde avgöra vad som var viktigt.
 */

export interface BesokVyProps {
  besok: MalindexBesok
  /** Brukarens övriga besök – bär målens texter och den skattning som viktade indexet. */
  alla: MalindexBesok[]
  config: MalindexConfig
  samtalslage: boolean
  onTillbaka: () => void
  /** Raderar besöket. Vyn stänger modalen; anroparen arkiverar och lämnar sidan. */
  onRadera: () => void
}

const Rubrik: React.FC<{ text: string }> = ({ text }) => (
  <p className="m-0 mb-2 mt-7 text-xs font-medium uppercase tracking-wide text-gray-500">{text}</p>
)


/**
 * En skattningsrad: område, ordet och siffran i sin färgade ruta.
 *
 * Samma visualisering som prioriteringssteget, av samma skäl: sorteringen ensam räcker
 * inte när flera rader har samma värde. `viktighet` styr bara vilka ord som används –
 * "Mycket viktigt" om hur hemtjänsten hjälpt betyder ingenting.
 */
const SkattningsRadVarde: React.FC<{
  varde: number | undefined
  config: MalindexConfig
  viktighet?: boolean
}> = ({ varde, config, viktighet }) => {
  if (varde === undefined) return <span className="shrink-0 text-sm italic text-gray-400">ej besvarad</span>
  if (varde === config.ejTillampligt.varde)
    return <span className="shrink-0 text-sm italic text-gray-400">{config.ejTillampligt.etikett}</span>

  const ord = viktighet ? VIKTIGT_ORD[varde] : config.skala.find((sk) => sk.varde === varde)?.etikett
  return (
    <>
      <span className="shrink-0 text-sm text-gray-600">{ord}</span>
      <Sifferruta varde={varde} />
    </>
  )
}

/**
 * Bara siffran, utan ord. Används i jämförelsetabellen, där två värden ska ställas mot
 * varandra och orden bara gör raden lång.
 *
 * Ej tillämpligt och obesvarat måste ändå skrivas ut – de har ingen siffra, och en tom
 * cell där skulle läsas som en nolla.
 */
const BaraSiffra: React.FC<{ varde: number | undefined; config: MalindexConfig }> = ({ varde, config }) => {
  if (varde === undefined) return <span className="text-sm italic text-gray-400">ej besvarad</span>
  if (varde === config.ejTillampligt.varde)
    return <span className="text-sm italic text-gray-400">{config.ejTillampligt.etikett}</span>
  return <Sifferruta varde={varde} />
}

/** Hela raden: områdets namn till vänster, svaret till höger. */
const SkattningsRad: React.FC<{
  namn: string
  varde: number | undefined
  config: MalindexConfig
  viktighet?: boolean
}> = ({ namn, varde, config, viktighet }) => (
  <>
    <span className="min-w-0 flex-1 text-base leading-snug text-gray-800">{namn}</span>
    <SkattningsRadVarde varde={varde} config={config} viktighet={viktighet} />
  </>
)

const BesokVy: React.FC<BesokVyProps> = ({ besok, alla, config, samtalslage, onTillbaka, onRadera }) => {
  /** Vilken knapp som nyss kopierade – null när ingen gjort det på ett tag. */
  const [kopierad, setKopierad] = React.useState<string | null>(null)
  const [raderaOppen, setRaderaOppen] = React.useState(false)
  const [notificationInstance, contextHolder] = notification.useNotification()

  const vagtMot = besok.index_viktat_av ? alla.find((b) => b.id === besok.index_viktat_av) : null

  /**
   * Uppföljningar vars Målindex viktades av det HÄR besökets behovsskattning.
   *
   * De förlorar sitt tal om besöket raderas – indexet lagras på uppföljningen, men
   * underlaget ligger här. Kedjan går ett steg: tas behoven om vid varje besök viktas
   * nästa uppföljning av det senare besöket, inte av det här.
   */
  const paverkade = React.useMemo(
    () =>
      alla
        .filter((b) => !b.draft && b.index_viktat_av === besok.id && b.index_total !== null)
        .map((b) => ({ id: b.id, datum: formateraDatum(b.created), index: (b.index_total as number).toFixed(1) })),
    [alla, besok.id]
  )

  const kopiera = async (varde: string, nyckel: string) => {
    try {
      await navigator.clipboard.writeText(varde)
      setKopierad(nyckel)
      window.setTimeout(() => setKopierad(null), 2500)
    } catch {
      // Urklippet kan vara blockerat. Texten står redan på skärmen, så det går att
      // markera för hand – men det måste sägas, annars ser knappen bara ut att hänga.
      notificationInstance.warning({
        message: 'Kunde inte kopiera automatiskt',
        description: 'Markera texten och kopiera för hand.',
      })
    }
  }

  /**
   * Målets text hämtas från det besök som SATTE målet – uppföljningen bär bara id:t.
   * Nyaste definitionen vinner: ett mål kan ha sparats om med samma id vid ett senare
   * besök, och då är den senare formuleringen den som gäller.
   */
  const malTextFor = (malId: string): string | null => {
    const bakifran = kronologiskt(alla).reverse()
    for (let i = 0; i < bakifran.length; i += 1) {
      const m = bakifran[i].mal?.find((x) => x.malId === malId)
      if (m) return m.maltext.trim() || m.egnaOrd.trim()
    }
    return null
  }

  /**
   * Bara målen som formulerades HÄR. `besok.mal` bär hela den gällande uppsättningen –
   * behållna mål ligger kvar i den besök efter besök – så utan det här filtret visades
   * ett mål från förra besöket som om det satts nu.
   *
   * Det behållna målet syns ändå, under "Mål som följdes upp", med sin gradering.
   */
  const formuleradeMal = React.useMemo(() => nyaMalVid(besok, alla), [besok, alla])

  const uppfoljda = besok.mal_uppfoljning ?? []
  const uppfyllelseOrd = (v: number | null) =>
    v === null ? 'ej besvarad' : config.skala.find((s) => s.varde === v)?.etikett ?? String(v)

  /**
   * Nyttan bredvid den viktighet den faktiskt vägdes mot – alltså skattningen från
   * det besök `index_viktat_av` pekar på, inte besökets egen framåtblickande behovsbild.
   * Sorterat på viktighet, så att det brukaren tyckte betydde mest står överst.
   */
  const nyttorader = React.useMemo(() => {
    if (!besok.nytta_svar) return []
    return config.omraden
      .map((omrade, ordning) => ({
        omrade,
        ordning,
        viktighet: svarForOmrade(vagtMot?.behov_svar, omrade.id),
        nytta: svarForOmrade(besok.nytta_svar, omrade.id),
      }))
      .filter((r) => r.viktighet !== undefined || r.nytta !== undefined)
      .sort(
        (a, b) =>
          efterVarde(b.viktighet, config.ejTillampligt.varde) -
            efterVarde(a.viktighet, config.ejTillampligt.varde) || a.ordning - b.ordning
      )
  }, [besok.nytta_svar, vagtMot, config.omraden, config.ejTillampligt.varde])

  /**
   * Besökets EGEN behovsskattning – den framåtblickande, som viktar nästa besök.
   * Sorterad som prioriteringssteget: viktigast överst, ej tillämpligt och obesvarat sist.
   */
  const behovsrader = React.useMemo(() => {
    if (!besok.behov_svar) return []
    return config.omraden
      .map((omrade, ordning) => ({ omrade, ordning, varde: svarForOmrade(besok.behov_svar, omrade.id) }))
      .sort(
        (a, b) =>
          efterVarde(b.varde, config.ejTillampligt.varde) - efterVarde(a.varde, config.ejTillampligt.varde) ||
          a.ordning - b.ordning
      )
  }, [besok.behov_svar, config.omraden, config.ejTillampligt.varde])

  // Lika breda värdekolumner, och innehållet centrerat i båda – rubriken hamnar då
  // rakt över siffran. Högerjusterat såg snett ut: rubrikordet är sex gånger bredare
  // än rutan, så deras vänsterkanter började på olika ställen.
  // 28 räcker: "ej tillämpligt" är det bredaste som kan stå där.
  const KOL = 'w-28 shrink-0 flex justify-center'

  return (
    <div className="max-w-4xl">
      {contextHolder}

      {/* Samma sidhuvud som "Målindex" på översikten – H3 plus avdelare. Vägen tillbaka
          ligger i högerkanten, så att rubriken är det översta på sidan. */}
      <ViewHeader
        heading={besoksEtikett(besok)}
        extra={
          <button
            type="button"
            onClick={onTillbaka}
            className="flex items-center gap-2 text-base text-pejlen-500 transition-colors hover:text-pejlen-400"
          >
            <ArrowLeftOutlined /> Tillbaka till översikten
          </button>
        }
      />

      {/* Allt som identifierar besöket på en rad. Egen sektion för svarande blev en
          rubrik över en enda mening. */}
      <p className="m-0 text-sm text-gray-500">
        {[
          besok.personnummer,
          formateraDatum(besok.created),
          besok.process_id ? `ÄrendeID ${besok.process_id}` : 'ÄrendeID saknas',
          besok.svarande ? `Svar lämnade av ${SVARANDE_ETIKETT[besok.svarande] ?? besok.svarande}` : null,
          // Bara när svaret är ja. Ett "Demensdiagnos: nej" på varje besök är brus –
          // frånvaron av orden säger samma sak.
          besok.demensdiagnos ? 'Demensdiagnos' : null,
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>

      {/*
        Indexrutan finns bara på uppföljningar. En kartläggning ger aldrig något index –
        det finns ingen tidigare skattning att vikta mot – och "inget index räknades ut"
        läses som att något gått fel i stället för som metodens normala läge.

        På en uppföljning betyder samma mening något: där SKULLE det ha gått, och att det
        inte gjorde det är information.
      */}
      {!arKartlaggning(besok) && (
        <div className="mt-4 text-base">
          {samtalslage ? (
            <span className="flex items-center gap-2 text-gray-500">
              <EyeInvisibleOutlined /> Målindex är dolt i samtalsläge — stäng av det i översikten
            </span>
          ) : besok.index_total !== null ? (
            // Etikett och tal i samma storlek och vikt – talet är sidans nyckelsiffra
            // och ska läsas som en enhet, inte som en etikett med ett tal efter.
            //
            // Vilken skattning som viktade indexet står kvar under nyttotabellen, där
            // den hör hemma: det är där de två mätningarna faktiskt ställs mot varandra.
            <span className="text-xl font-semibold text-gray-800">
              Målindex {besok.index_total.toFixed(1)}
            </span>
          ) : (
            <span className="italic text-gray-500">Inget index räknades ut för besöket</span>
          )}
        </div>
      )}

      {uppfoljda.length > 0 && (
        <>
          <Rubrik text="Mål som följdes upp" />
          <div className="flex flex-col gap-3">
            {uppfoljda.map((u, i) => {
              const maltext = malTextFor(u.malId)
              const nyckel = `mal-${u.malId}`
              return (
                /*
                  Två spalter, inte två rader. Låg knappen på en egen rad ovanför texten
                  sköt dess 44px ned allt som kom efter, och måltexten hamnade långt
                  under sin egen etikett. Nu bär vänsterspalten hela innehållet medan
                  knappen står vid sidan, och texten börjar direkt under taggen.
                */
                <div
                  key={u.malId}
                  className="flex max-w-3xl items-start justify-between gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3"
                >
                  {/* Lika avstånd mellan alla tre genom gap i stället för olika mt på
                      varje rad. Taggen ligger i en egen div så att dess radbox inte
                      äter av utrymmet, och båda styckena har samma radhöjd – annars
                      skiljer sig glappen fast marginalerna är lika. */}
                  <div className="flex min-w-0 flex-1 flex-col gap-2.5">
                    <div>
                      <Tag color="blue">{uppfoljda.length > 1 ? `Mål ${i + 1}` : 'Mål'}</Tag>
                    </div>

                    <p className="m-0 text-base leading-snug text-gray-800">
                      {maltext ?? <span className="italic text-gray-500">Målets text hittades inte</span>}
                    </p>
                    <p className="m-0 text-base leading-snug text-gray-800">
                      Uppfyllelse: {uppfyllelseOrd(u.uppfyllelse)}
                      {' · '}
                      {u.behall === null ? (
                        <span className="italic text-gray-500">behåll ej besvarad</span>
                      ) : (
                        <span className="font-medium">{u.behall ? 'Målet behölls' : 'Målet avslutades'}</span>
                      )}
                    </p>
                  </div>

                  {maltext && (
                    <Button
                      onClick={() => kopiera(maltext, nyckel)}
                      style={{ height: 44, fontSize: 16, paddingInline: 20 }}
                    >
                      {kopierad === nyckel ? 'Kopierat' : 'Kopiera mål'}
                    </Button>
                  )}
                </div>
              )
            })}
          </div>
        </>
      )}

      {formuleradeMal.length > 0 && (
        <>
          <Rubrik text="Mål formulerade vid besöket" />
          <div className="flex flex-col gap-3">
            {formuleradeMal.map((m, i) => {
              const maltext = m.maltext.trim() || m.egnaOrd.trim()
              const nyckel = `satt-${m.malId}`
              return (
                // Samma tvåspaltiga box som under "Mål som följdes upp" – knappen vid
                // sidan i stället för ovanför, så att texten börjar direkt under taggen.
                <div
                  key={m.malId}
                  className="flex max-w-3xl items-start justify-between gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3"
                >
                  <div className="flex min-w-0 flex-1 flex-col gap-2.5">
                    <div>
                      <Tag color="blue">{formuleradeMal.length > 1 ? `Mål ${i + 1}` : 'Mål'}</Tag>
                    </div>
                    <p className="m-0 text-base leading-snug text-gray-800">{maltext}</p>
                    {m.maltext.trim() && m.egnaOrd.trim() && (
                      <p className="m-0 text-base italic leading-snug text-gray-800">”{m.egnaOrd.trim()}”</p>
                    )}
                  </div>

                  {/* Kopierar BARA målformuleringen, inte de egna orden – det är den
                      texten som ska in i beställningen. */}
                  <Button
                    onClick={() => kopiera(m.maltext.trim() || m.egnaOrd.trim(), nyckel)}
                    style={{ height: 44, fontSize: 16, paddingInline: 20 }}
                  >
                    {kopierad === nyckel ? 'Kopierat' : 'Kopiera mål'}
                  </Button>
                </div>
              )
            })}
          </div>
        </>
      )}

      {besok.prioriterade && besok.prioriterade.length > 0 && (
        <>
          <Rubrik text="Prioriterade områden" />
          <p className="m-0 text-base text-gray-800">
            {besok.prioriterade
              .map((id) => config.omraden.find((o) => o.id === id)?.namn)
              .filter(Boolean)
              .join(' · ')}
          </p>
        </>
      )}

      {nyttorader.length > 0 && (
        <>
          <Rubrik text="Nytta mot viktighet" />
          <div className="max-w-3xl overflow-hidden rounded-lg border border-gray-100">
            {/* Bara siffror i värdekolumnerna. Orden gjorde raderna långa och drog
                blicken bort från jämförelsen, som är hela poängen med tabellen –
                kolumnrubriken säger redan vilken skala det är. */}
            <div className="flex gap-4 border-b border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-500">
              <span className="min-w-0 flex-1">Område</span>
              <span className={KOL}>Viktighet</span>
              <span className={KOL}>Nytta</span>
            </div>
            {nyttorader.map((r, i) => (
              <div
                key={r.omrade.id}
                className={`flex items-center gap-4 px-3 py-2 ${i % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`}
              >
                <span className="min-w-0 flex-1 text-base leading-snug text-gray-800">{r.omrade.namn}</span>
                <span className={KOL}>
                  <BaraSiffra varde={r.viktighet} config={config} />
                </span>
                <span className={KOL}>
                  <BaraSiffra varde={r.nytta} config={config} />
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {besok.behov_svar && (
        <>
          <Rubrik text="Behovsskattning vid besöket" />
          <div className="max-w-3xl overflow-hidden rounded-lg border border-gray-100">
            {behovsrader.map((r, i) => (
              <div
                key={r.omrade.id}
                className={`flex items-center gap-3 px-3 py-2 ${i % 2 === 1 ? 'bg-gray-50' : 'bg-white'}`}
              >
                <SkattningsRad namn={r.omrade.namn} varde={r.varde} config={config} viktighet />
              </div>
            ))}
          </div>
        </>
      )}

      {besok.note && besok.note.trim() && (
        <>
          <Rubrik text="Anteckning" />
          <p className="m-0 max-w-3xl text-base leading-snug text-gray-800">{besok.note}</p>
        </>
      )}

      {/*
       * Raderingen ligger sist och bakom en linje, avskild från allt som är innehåll.
       * Ingen fyllning: rött märker att handlingen inte går att ångra, det ska inte
       * dra ögat dit. Samma regel som i modalernas knappar, se modalStil.
       */}
      <div className="mt-12 max-w-3xl border-t border-gray-200 pt-4">
        <Button danger onClick={() => setRaderaOppen(true)}>
          Radera besök
        </Button>
      </div>

      <RaderaBesokModal
        open={raderaOppen}
        sort="sparat"
        datum={formateraDatum(besok.created)}
        paverkade={paverkade}
        onBekrafta={() => {
          setRaderaOppen(false)
          onRadera()
        }}
        onAvbryt={() => setRaderaOppen(false)}
      />
    </div>
  )
}

export default BesokVy
